import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { toFiniteOrNull } from "../utils/normalize";
import { formatYenCompact } from "../utils/format";

export interface TimeSeriesSeries {
  label: string;
  data: Array<number | null>;
}

// 有報/gBizの時系列指標を折れ線で可視化する。表と併記して使う。
export const TimeSeriesChart: React.FC<{
  title: string;
  icon?: React.ReactNode;
  labels: string[];
  series: TimeSeriesSeries[];
}> = ({ title, icon, labels: rawLabels, series: rawSeries }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // 外部データの型ゆらぎ対策: ラベルは文字列、値は有限の number か null に正規化し、
  // 各系列の長さをラベル数に揃える（NaN/Infinity/文字列で軸計算が壊れるのを防ぐ）。
  const labels = rawLabels.map((l) => (l == null ? "—" : String(l)));
  const series = rawSeries.map((s) => ({
    ...s,
    data: labels.map((_, i) => toFiniteOrNull(s.data[i])),
  }));
  // 全系列が空なら描画しない。
  const hasData = series.some((s) => s.data.some((v) => v != null));
  if (labels.length === 0 || !hasData) return null;

  // 金額は億/万で読みやすく整形（軸・ツールチップ共通。単位「円」は付けない）。
  const formatValue = (value: number | null): string =>
    formatYenCompact(value, "");

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {icon != null && (
          <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
        )}
        <Typography variant="h6">{title}</Typography>
      </Stack>
      <Box sx={{ width: "100%" }}>
        <LineChart
          height={isMobile ? 240 : 320}
          xAxis={[{ scaleType: "point", data: labels }]}
          yAxis={[{ valueFormatter: (v: number) => formatValue(v) }]}
          series={series.map((s) => ({
            label: s.label,
            data: s.data,
            valueFormatter: (v: number | null) => formatValue(v),
            connectNulls: true,
            showMark: true,
          }))}
          margin={{
            left: isMobile ? 52 : 64,
            right: 16,
            top: 24,
            bottom: isMobile ? 40 : 32,
          }}
          slotProps={{
            legend: {
              direction: "row",
              position: { vertical: "top", horizontal: "middle" },
              itemMarkWidth: 10,
              itemMarkHeight: 10,
              labelStyle: { fontSize: isMobile ? 10 : 12 },
            },
          }}
        />
      </Box>
    </Box>
  );
};
